import * as React from "react";
import {ApplicationState} from "../../reducers";
import {connect} from "react-redux";
import {FormGroup, FormControl, ControlLabel, Col, Row, Button, Table, Modal, Checkbox} from "react-bootstrap";
import {LoadingOverlay} from "../Loading";

import {TicketsState} from "../../reducers/tickets";
import {
    deleteTicket,
    getTicketList,
    saveNewTicket,
    TicketAction,
    updateNewTicket,
    updateTicket,
    setShowInactive,
    getDeltaList,
    updateNewDelta,
    saveNewDelta,
    updateDelta,
    deleteDelta,
} from "../../actions/tickets";
import {defaultTicket, destinations, Search, Ticket} from "../../models/hotels";
import {clone, groupBy, keys, difference, compact, intersection, head, isNaN, isNil} from "lodash";
import * as moment from "moment";
import {moneyFormat} from "../../utils";
import {ticketNames} from "../../enums";
import {defaultDiscountCode, TicketPriceDelta} from "../../models/order";

interface TicketAdminProps extends TicketsState {
    getTicketList: () => Promise<TicketAction>;
    updateNewTicket: (ticket: Ticket) => TicketAction;
    setShowInactive: (show: boolean) => TicketAction;
    saveNewTicket: (ticket: Ticket) => Promise<TicketAction>;
    updateTicket: (ticket: Ticket) => Promise<TicketAction>;
    deleteTicket: (ticket: Ticket) => Promise<TicketAction>;

    getDeltaList: () => Promise<TicketAction>;
    updateNewDelta: (delta: TicketPriceDelta) => TicketAction;
    saveNewDelta: (delta: TicketPriceDelta) => Promise<TicketAction>;
    updateDelta: (delta: TicketPriceDelta) => Promise<TicketAction>;
    deleteDelta: (delta: TicketPriceDelta) => Promise<TicketAction>;
}

class TicketAdmin extends React.Component<TicketAdminProps> {
    componentDidMount(): void {
        this.updateTicketList();
    }

    updateTicketList() {
        this.props.getTicketList();
        this.props.getDeltaList();
    }


    public render(): JSX.Element {
        let {tickets, newTicket, loading, saveLoading, updateNewTicket, showInactive} = this.props;
        if (!tickets) {
            tickets = [];
        }
        if (loading) {
            return <LoadingOverlay/>;
        }

        let ticketsToShow = tickets;
        if (!showInactive) {
            ticketsToShow = tickets.filter(t => t.active);
        }

        return (
            <div>
                <TicketEditModal close={() => updateNewTicket(null)}
                                 ticket={newTicket}
                                 deltas={this.props.deltas}
                                 change={this.updateTicket.bind(this)}
                                 save={this.save.bind(this)}
                                 isOpen={!!newTicket}/>
                <Row>
                    <Col xs={6}>
                        <FormGroup>
                            <Checkbox checked={showInactive}
                                      onChange={(e: any) => {
                                          this.props.setShowInactive(e.target.checked);
                                      }}>
                                Show Inactive Tickets
                            </Checkbox>
                        </FormGroup>
                    </Col>
                    <Col xs={6} className="text-right margin-bottom">
                        <Button bsStyle="success" onClick={() => updateNewTicket(defaultTicket())}>
                            <i className="fa fa-plus" /> Add new Ticket Type
                        </Button>
                    </Col>
                </Row>
                <Row>
                    <Col xs={12}>
                        <Table bordered striped hover>
                            <thead>
                            <tr>
                                <th>Ticket Type</th>
                                <th>Destination</th>
                                <th>Days</th>
                                <th>Standard Adult Price</th>
                                <th>Standard Child Price</th>
                                <th>Valid Range</th>
                                <th>Active</th>
                                <th>Created On</th>
                                <th>Actions</th>
                            </tr>
                            </thead>
                            <tbody>
                            {ticketsToShow.map(ticket => {
                                return (
                                    <tr key={ticket.id}>
                                        <td>{ticket.name}</td>
                                        <td>{ticket.destination}</td>
                                        <td>{ticket.days}</td>
                                        <td className="text-right">{ticket.adultPrice !== null && moneyFormat(ticket.adultPrice)}</td>
                                        <td className="text-right">{ticket.childPrice !== null && moneyFormat(ticket.childPrice)}</td>
                                        <td>{ticket.validRange}</td>
                                        <td>{ticket.active ? "Active" : "Inactive"}</td>
                                        <td>{moment(ticket.createdOn).format("LL")}</td>
                                        <td className="action-buttons">
                                            <Button className="hint--bottom"
                                                    onClick={() => {updateNewTicket(ticket)}}
                                                    aria-label="Edit Ticket">
                                                <i className="fa fa-edit"  />
                                            </Button>
                                            <Button className="hint--bottom"
                                                    onClick={() => this.deactivateTicket(ticket)}
                                                    aria-label="Deactivate Ticket">
                                                <i className="fa fa-remove red" />
                                            </Button>
                                        </td>
                                    </tr>
                                );
                            })}
                            </tbody>
                        </Table>
                    </Col>
                </Row>
            </div>
        );
    }

    private save(ticket: Ticket, deltas: TicketPriceDelta[]) {
        const {newTicket, saveNewTicket, updateTicket} = this.props;
        if (newTicket && newTicket.id) {
            updateTicket(newTicket);
            if (deltas && deltas.length > 0) {
                this.saveDeltas(ticket, deltas);
            }
        } else {
            saveNewTicket(newTicket);
        }
    }

    private saveDeltas(ticket: Ticket, newDeltas: TicketPriceDelta[]) {
        const ticketDeltasById =  groupBy(this.props.deltas.filter(d => d.ticketId === ticket.id), "id");
        const ticketDeltaIds = compact(keys(ticketDeltasById));
        const newDeltasById = groupBy(newDeltas, "id");
        const newDeltaIds = compact(keys(newDeltasById));

        const idsToDelete = difference(ticketDeltaIds, newDeltaIds);
        const idsToUpdate = intersection(ticketDeltaIds, newDeltaIds);
        const toCreate: TicketPriceDelta[] = newDeltasById["undefined"] || [];
        const toUpdate: TicketPriceDelta[] = idsToUpdate.map(id => head(newDeltasById[id]));
        const toDelete: TicketPriceDelta[] = idsToDelete.map(id => head(ticketDeltasById[id]));

        toCreate.forEach(t => this.props.saveNewDelta(t));
        toUpdate.forEach(t => this.props.updateDelta(t));
        toDelete.forEach(t => this.props.deleteDelta(t));
    }

    private updateTicket(f: (s: Ticket) => void) {
        const s = clone(this.props.newTicket);
        f(s);
        this.props.updateNewTicket(s);
    }

    private deactivateTicket(d: Ticket): void {
        if (confirm(`Are you sure you want to deactivate ${d.name}?`)) {
            d.active = false;
            this.props.updateTicket(clone(d));
        }
    }
}


const connected = connect((state: ApplicationState) => ({
    ...state.tickets
}), {
    getTicketList,
    updateNewTicket,
    saveNewTicket,
    updateTicket,
    deleteTicket,
    setShowInactive,
    getDeltaList,
    updateNewDelta,
    saveNewDelta,
    updateDelta,
    deleteDelta,
})(TicketAdmin);

export default connected;


interface EditModalProps {
    ticket: Ticket;
    change: (f: (ticket: Ticket) => void) => void;
    save: (ticket: Ticket, deltas: TicketPriceDelta[]) => void;
    close: () => void;
    isOpen: boolean;
    deltas: TicketPriceDelta[];
}

interface EditModalState {
    focusedInput: any;
    deltas: TicketPriceDelta[];
    ticketId: string;
}

class TicketEditModal extends React.Component<EditModalProps, EditModalState> {

    constructor(props: EditModalProps, context: any) {
        super(props, context);
        this.state = {
            focusedInput: null,
            ticketId: null,
            deltas: []
        };
    }

    componentDidUpdate(prevProps: Readonly<EditModalProps>, prevState: Readonly<EditModalState>, prevContext: any): void {
        this.setDeltasFromProps();
    }

    private setDeltasFromProps() {
        const ticketId = this.props.ticket ? this.props.ticket.id : null;
        if (this.state.ticketId !== ticketId) {
            this.setState({
                ticketId,
                deltas: this.props.deltas.filter(d => d.ticketId === ticketId).map(clone),
            } as EditModalState);
        }
    }

    public render(): JSX.Element {
        const {isOpen, close, change, ticket, save} = this.props;
        const {deltas} = this.state;
        if (!ticket) {
            return null;
        }
        const title = ticket.id ? "Update" : "Create";
        return (
            <Modal show={isOpen} onHide={() => close()}>
                <Modal.Header>
                    <Modal.Title>{title} Ticket</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <div style={{margin: "10px", clear: "both"}}>
                        <Row>
                            <Col>
                                <FormGroup>
                                    <ControlLabel>Ticket Type*</ControlLabel>
                                    <FormControl componentClass="select"
                                                 placeholder="Ticket Type"
                                                 value={ticket.name}
                                                 onChange={(e: any) =>
                                                     change((s: Ticket) => s.name = e.target.value)}>
                                        {ticketNames.map(d => <option value={d.code} key={d.code}>{d.name}</option>)}
                                    </FormControl>
                                </FormGroup>
                            </Col>
                        </Row>
                        <Row>
                            <Col>
                                <FormGroup>
                                    <ControlLabel>Destination*</ControlLabel>
                                    <FormControl componentClass="select"
                                                 placeholder="Destination"
                                                 value={ticket.destination}
                                                 onChange={(e: any) =>
                                                     change((s: Ticket) => s.destination = e.target.value)}>
                                        {destinations.map(d => <option value={d.code} key={d.code}>{d.name}</option>)}
                                    </FormControl>
                                </FormGroup>
                            </Col>
                        </Row>
                        <Row>
                            <Col md={4}>
                                <FormGroup>
                                    <ControlLabel>Days</ControlLabel>
                                    <FormControl type="number"
                                                 min={3}
                                                 max={15}
                                                 value={ticket.days}
                                                 onChange={(e: any) =>
                                                     change((u: Ticket) => u.days = parseInt(e.target.value))}/>
                                </FormGroup>
                            </Col>
                            <Col md={4}>
                                <FormGroup>
                                    <ControlLabel>Adult Price</ControlLabel>
                                    <FormControl type="number"
                                                 value={ticket.adultPrice || ""}
                                                 onChange={(e: any) =>
                                                     change((u: Ticket) => u.adultPrice = parseFloat(e.target.value))}/>
                                </FormGroup>
                            </Col>
                            <Col md={4}>
                                <FormGroup>
                                    <ControlLabel>Child Price</ControlLabel>
                                    <FormControl type="number"
                                                 value={ticket.childPrice || ""}
                                                 onChange={(e: any) =>
                                                     change((u: Ticket) => u.childPrice = parseFloat(e.target.value))}/>
                                </FormGroup>
                            </Col>
                        </Row>
                        <Row>
                            <Col>
                                <FormGroup>
                                    <ControlLabel>Valid Range</ControlLabel>
                                    <FormControl type="number"
                                                 value={ticket.validRange || ""}
                                                 onChange={(e: any) =>
                                                     change((u: Ticket) => u.validRange = parseInt(e.target.value))}/>
                                </FormGroup>
                            </Col>
                        </Row>
                        <Row>
                            <Col>
                                <FormGroup>
                                    <ControlLabel>Is Active</ControlLabel>
                                    <FormControl type="checkbox"
                                                 checked={ticket.active}
                                                 onChange={(e: any) =>
                                                     change((u: Ticket) => u.active = e.target.checked)}/>
                                </FormGroup>
                            </Col>
                        </Row>
                        {!this.state.ticketId ? null : (<Row>
                            <Col>
                                <h3>Price Adjustments</h3>

                                <div className="adjustment-calc">
                                    <label>Ticket prices are calculated with: </label>
                                    <br/>
                                    (<span>"Disney price w/tax"</span> -
                                    (<span>"Disney price wo/tax"</span> * <span>"Multiplier"</span>) + <span>"Adjustment"</span>) *
                                    <span> "Quantity"</span>
                                </div>
                                <div className="text-right">
                                    <Button bsSize="xsmall" bsClass="default" onClick={() => {
                                        deltas.push({ticketId: this.state.ticketId} as TicketPriceDelta);
                                        this.setState({deltas})
                                    }}>
                                        + Add Adjustment
                                    </Button>
                                </div>
                                <Table bordered striped hover>
                                    <thead>
                                        <tr>
                                            <th>Form</th>
                                            <th>Multiplier</th>
                                            <th>Adult Adjustment</th>
                                            <th>Child Adjustment</th>
                                            <th>Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                    {deltas.map((delta, ix) => {
                                        return (
                                            <tr key={delta.id || ix}>
                                                <td>
                                                    <FormControl
                                                        type="text"
                                                        value={delta.form || ""}
                                                        onChange={(e: any) => {
                                                            delta.form = e.target.value;
                                                            this.setState({deltas})
                                                        }}
                                                    />
                                                </td>
                                                <td>
                                                    <FormControl
                                                        type="number"
                                                        min={0}
                                                        step="0.01"
                                                        value={isNil(delta.multiplier) ? "" : delta.multiplier}
                                                        onChange={(e: any) => {
                                                            delta.multiplier = parseFloat(e.target.value);
                                                            this.setState({deltas})
                                                        }}
                                                    />
                                                </td>
                                                <td>
                                                    <FormControl
                                                        type="number"
                                                        min={0}
                                                        step="0.01"
                                                        value={isNil(delta.adultAdjustment) ? "" : delta.adultAdjustment}
                                                        onChange={(e: any) => {
                                                            delta.adultAdjustment = parseFloat(e.target.value);
                                                            if (isNaN(delta.adultAdjustment)) {
                                                                delta.adultAdjustment = e.target.value;
                                                            }
                                                            this.setState({deltas})
                                                        }}
                                                    />
                                                </td>
                                                <td>
                                                    <FormControl
                                                        type="number"
                                                        min={0}
                                                        step="0.01"
                                                        value={isNil(delta.childAdjustment) ? "" : delta.childAdjustment}
                                                        onChange={(e: any) => {
                                                            delta.childAdjustment = parseFloat(e.target.value);
                                                            this.setState({deltas})
                                                        }}
                                                    />
                                                </td>
                                                <td>
                                                    <Button className="hint--bottom"
                                                            onClick={() =>{
                                                                const ix = deltas.indexOf(delta);
                                                                deltas.splice(ix, 1);
                                                                this.setState({deltas});
                                                            }}
                                                            aria-label="Delete Adjustment">
                                                        <i className="fa fa-remove red" />
                                                    </Button>
                                                </td>
                                            </tr>
                                        )
                                    })}
                                    </tbody>
                                </Table>
                            </Col>
                        </Row>)}
                    </div>
                </Modal.Body>
                <Modal.Footer>
                    <Button bsStyle="success" onClick={() => close()}>Cancel</Button>
                    <Button bsStyle="success" onClick={() => save(ticket, deltas)}>Save Changes</Button>
                </Modal.Footer>
            </Modal>
        );
    }
}




// WEBPACK FOOTER //
// ./src/components/admin/TicketAdmin.tsx